import React, { useMemo, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'

import { Spinner } from '../../../interaxDesignSystemComponents'
import { PageWrapper } from '../../../shared/components'
import { INTERAX_COLORS } from '../../../constants/colors'
import { useTranslation } from '../../../shared/hooks/useIntractionTranslate'

import useAppState from '../../../hooks/useAppState'
import { useOrganizationDetail } from '../../../apiLayer/queryHooks/queries'
import { useCompleteOrganizationOnboarding } from '../../../apiLayer/queryHooks/mutations'

import { isNonOperationalOrganizationStatus, OrganizationStatus } from 'pure-js/libs/organizationStatus'
import { OrgRole } from '../../../../../pure-js/types/OrganizationMembership'

import { useOrgOnboardingWizard } from './hooks/useOrgOnboardingWizard'
import { type OnboardingStepProgressItem } from './components/OnboardingStepProgress'
import { OrgOnboardingScaffold } from './components/OrgOnboardingScaffold'
import { OrgIdentityStep } from './steps/OrgIdentityStep'
import { OrgAddressStep } from './steps/OrgAddressStep'
import { OrgReviewStep } from './steps/OrgReviewStep'
import type { OnboardingFormValues, OnboardingStepIndex } from './types'

/** Maps a raw backend error code to friendly copy so codes never reach the user. */
function resolveOnboardingSubmitMessage(t: (key: string) => string, rawError: string): string {
  const raw = rawError.toLowerCase()
  if (raw.includes('invite-not-found-or-used')) return t('org.invite.errors.usedTitle')
  if (raw.includes('invite-email-mismatch')) return t('org.invite.errors.emailMismatchTitle')
  if (raw.includes('invite-expired')) return t('org.invite.errors.expiredTitle')
  if (raw.includes('capacity') || raw.includes('member limit')) return t('org.invite.errors.capacityTitle')
  return t('org.onboarding.activationFailedGeneric')
}

/**
 * Onboarding wizard at `/org/:orgId/onboarding`. Three steps for a PENDING_ONBOARDING
 * org admin to finalize their organization (name → address → branding/review).
 * On Activate, the backend flips org `status` → `ACTIVE`.
 *
 * Guards (in order):
 *   1. `orgId` param required (else `/`)
 *   2. Auth must be initialized; user must be loaded (loading state)
 *   3. Org detail must load (loading state)
 *   4. User must be admin of the URL tenant (else `/`)
 *   5. Org status must be `PENDING_ONBOARDING` — `ACTIVE` redirects to /facilitator;
 *      `DEACTIVATED` / `ARCHIVED` show an error card.
 *
 * Uses a slim onboarding-specific shell (`OnboardingStepProgress` + `OnboardingStepNav`)
 * rather than reusing `FlowContainer` from the reflection wizard. FlowContainer is
 * generic in name but its `FlowBottomComponentWrapper` is rife with reflection /
 * sprint / report-specific draft/template logic — porting that for a 3-step
 * onboarding form would be heavier than this shim. Visual cadence still matches
 * (numbered step circles + connector + sun accent + same bottom bar layout).
 */
export const OrgOnboardingWizard: React.FC = () => {
  const { orgId } = useParams<{ orgId: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { state } = useAppState()

  const orgDetail = useOrganizationDetail(orgId)
  const completeMutation = useCompleteOrganizationOnboarding()

  const wizard = useOrgOnboardingWizard()
  const { form, step, goNext, goBack, goToStep, isLastStep } = wizard
  const [submitError, setSubmitError] = useState<string | null>(null)
  const org = orgDetail.data?.data
  const membership = state.user.organizations?.find((m) => m.organizationId === orgId)

  // Step config (labels resolved via translations).
  const stepItems: OnboardingStepProgressItem[] = useMemo(
    () => [
      { id: 'identity', label: t('org.onboarding.stepIdentityShort') },
      { id: 'address', label: t('org.onboarding.stepAddressShort') },
      { id: 'review', label: t('org.onboarding.stepReviewShort') }
    ],
    [t]
  )

  // 1. Missing orgId — bounce to root.
  if (!orgId) {
    return <Navigate to="/" replace />
  }

  // 2. Wait for auth initialization.
  if (!state.isAuthInitialized) {
    return (
      <PageWrapper>
        <LoadingPanel message={t('org.onboarding.loading')} />
      </PageWrapper>
    )
  }

  // No user — bounce to login, preserving the onboarding deep-link as the
  // "from" so the user lands back here after sign-in.
  if (!state.user?.id) {
    return <Navigate to="/login" replace state={{ from: { pathname: `/org/${orgId}/onboarding` } }} />
  }

  // 3. Wait for org detail.
  if (orgDetail.isLoading) {
    return (
      <PageWrapper>
        <LoadingPanel message={t('org.onboarding.loading')} />
      </PageWrapper>
    )
  }

  if (!org) {
    return (
      <PageWrapper>
        <ErrorPanel title={t('org.onboarding.notFoundTitle')} description={t('org.onboarding.notFoundDescription')} />
      </PageWrapper>
    )
  }

  // 4. Admin of THIS org. Platform super-admins (no membership row) are not
  //    treated as admins of an arbitrary org — they don't go through onboarding.
  const isAdminOfOrg = membership?.role === OrgRole.ADMIN
  if (!isAdminOfOrg) {
    return (
      <PageWrapper>
        <ErrorPanel title={t('org.onboarding.notAdminTitle')} description={t('org.onboarding.notAdminDescription')} />
      </PageWrapper>
    )
  }

  // 5. Status routing.
  if (org.status === OrganizationStatus.ACTIVE) {
    return <Navigate to="/facilitator" replace />
  }
  if (isNonOperationalOrganizationStatus(org.status)) {
    return (
      <PageWrapper>
        <ErrorPanel title={t('org.onboarding.suspendedTitle')} description={t('org.onboarding.suspended')} />
      </PageWrapper>
    )
  }
  if (org.status !== OrganizationStatus.PENDING_ONBOARDING) {
    // Defensive — unknown status. Treat as "already active" rather than crash.
    return <Navigate to="/facilitator" replace />
  }

  const handlePrimary = async () => {
    setSubmitError(null)

    if (!isLastStep) {
      await goNext()
      return
    }

    // Final step — validate everything via handleSubmit, then call the mutation.
    await form.handleSubmit(async (values: OnboardingFormValues) => {
      const trimmedLogo = values.logo.trim()
      const trimmedPostalCode = values.postalCode.trim()
      try {
        const res = await completeMutation.mutateAsync({
          organizationId: orgId,
          payload: {
            name: values.name.trim(),
            address: {
              street: values.street.trim(),
              city: values.city.trim(),
              country: values.country.trim(),
              postalCode: trimmedPostalCode || undefined
            },
            logo: trimmedLogo || undefined
          }
        })
        if (!res.success) {
          setSubmitError(res.error || t('org.onboarding.activationFailedGeneric'))
          return
        }
        navigate('/facilitator', { replace: true })
      } catch (e) {
        const message = e instanceof Error ? e.message : t('org.onboarding.activationFailedGeneric')
        setSubmitError(message)
      }
    })()
  }

  const primaryLabel = isLastStep ? t('org.onboarding.activate') : t('org.onboarding.next')

  const mobileStepLabel = `${t('org.onboarding.stepLabel', {
    current: step + 1,
    total: stepItems.length
  })} — ${stepItems[step]?.label ?? ''}`

  return (
    <OrgOnboardingScaffold
      preTitle={t('org.onboarding.preTitle')}
      title={t('org.onboarding.title')}
      subtitle={t('org.onboarding.subtitle')}
      steps={stepItems}
      currentStep={step}
      mobileStepLabel={mobileStepLabel}
      errorMessage={submitError ? resolveOnboardingSubmitMessage(t, submitError) : null}
      nav={{
        isFirstStep: step === 0,
        isLastStep,
        primaryLoading: completeMutation.isPending,
        primaryLabel,
        backLabel: t('common.back'),
        onBack: goBack,
        onPrimary: handlePrimary
      }}
    >
      {step === 0 && <OrgIdentityStep control={form.control} errors={form.formState.errors} />}
      {step === 1 && <OrgAddressStep control={form.control} errors={form.formState.errors} />}
      {step === 2 && (
        <OrgReviewStep watch={form.watch} onEditStep={(target: OnboardingStepIndex) => goToStep(target)} />
      )}
    </OrgOnboardingScaffold>
  )
}

const LoadingPanel: React.FC<{ message: string }> = ({ message }) => (
  <Box
    role="status"
    aria-busy="true"
    sx={{
      width: '100%',
      maxWidth: 520,
      mx: 'auto',
      backgroundColor: INTERAX_COLORS.heritageBlue,
      border: `1px solid ${INTERAX_COLORS.white10}`,
      borderRadius: 3,
      px: { xs: 3, sm: 4, md: 5 },
      py: { xs: 4, sm: 5, md: 6 },
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center'
    }}
  >
    <Box
      sx={{
        width: 80,
        height: 80,
        borderRadius: '50%',
        backgroundColor: INTERAX_COLORS.sun20,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mb: 2.5
      }}
    >
      <Spinner />
    </Box>
    <Typography sx={{ color: INTERAX_COLORS.white, fontSize: 16, fontWeight: 500 }}>{message}</Typography>
  </Box>
)

const ErrorPanel: React.FC<{ title: string; description: string }> = ({ title, description }) => (
  <Box
    role="alert"
    sx={{
      width: '100%',
      maxWidth: 520,
      mx: 'auto',
      backgroundColor: INTERAX_COLORS.heritageBlue,
      border: `1px solid ${INTERAX_COLORS.white10}`,
      borderRadius: 3,
      px: { xs: 3, sm: 4, md: 5 },
      py: { xs: 4, sm: 5, md: 6 },
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center'
    }}
  >
    <Box
      sx={{
        width: 80,
        height: 80,
        borderRadius: '50%',
        backgroundColor: INTERAX_COLORS.redErrorBackground,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mb: 2.5
      }}
    >
      <ExclamationTriangleIcon style={{ width: 44, height: 44, color: INTERAX_COLORS.redError }} />
    </Box>
    <Typography
      sx={{
        color: INTERAX_COLORS.white,
        fontSize: { xs: 20, sm: 24 },
        fontWeight: 600,
        mb: 1
      }}
    >
      {title}
    </Typography>
    <Typography sx={{ color: INTERAX_COLORS.white70, fontSize: 14, lineHeight: 1.5 }}>{description}</Typography>
  </Box>
)
